import React from 'react';
import './LegalPages.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-content">
          <h1>Terms of Service</h1>
          <p className="legal-updated">Last updated: January 20, 2026</p>

          <section>
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using PrivateSold ("Service"), you agree to be bound by these 
              Terms of Service ("Terms"). If you do not agree to these Terms, please do not 
              use our Service.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              PrivateSold provides tools to generate bills of sale and invoices for private 
              party transactions. Our Service helps users who buy and sell items through 
              platforms like Craigslist, Facebook Marketplace, OfferUp, and similar marketplaces 
              create documentation for their transactions.
            </p>
            <p>
              <strong>Important:</strong> PrivateSold is a document generation tool only. 
              The documents created are for record-keeping purposes and to help document 
              private party transactions. We do not provide legal, tax, or financial advice.
            </p>
          </section>

          <section>
            <h2>3. User Accounts</h2>
            <h3>3.1 Registration</h3>
            <p>
              To access certain features, you must create an account. You agree to provide 
              accurate, current, and complete information during registration and to update 
              such information to keep it accurate.
            </p>
            
            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account 
              credentials and for all activities that occur under your account. Notify us 
              immediately of any unauthorized use.
            </p>
            
            <h3>3.3 Age Requirement</h3>
            <p>
              You must be at least 18 years old to use this Service. By using PrivateSold, 
              you represent that you are at least 18 years of age.
            </p>
          </section>

          <section>
            <h2>4. Service Tiers</h2>
            <h3>4.1 Free Tier</h3>
            <ul>
              <li>Unlimited bill of sale and invoice generation</li>
              <li>Immediate PDF download</li>
              <li>Advertisements displayed via Google AdSense</li>
              <li>Documents are not stored on our servers</li>
            </ul>
            
            <h3>4.2 Premium Tier</h3>
            <ul>
              <li>All free tier features, plus:</li>
              <li>Ad-free experience</li>
              <li>Document history and storage</li>
              <li>E-signature functionality</li>
              <li>Priority support</li>
            </ul>
          </section>

          <section>
            <h2>5. Subscription and Payments</h2>
            <h3>5.1 Billing</h3>
            <p>
              Premium subscriptions are billed in advance on a monthly or annual basis. 
              Payment is processed securely through Stripe.
            </p>
            
            <h3>5.2 Automatic Renewal</h3>
            <p>
              Subscriptions automatically renew unless cancelled before the end of the 
              current billing period. You can cancel at any time through your account settings.
            </p>
            
            <h3>5.3 Refunds</h3>
            <p>
              Subscription fees are generally non-refundable. However, we may offer refunds 
              at our discretion on a case-by-case basis. Contact support for assistance.
            </p>
            
            <h3>5.4 Price Changes</h3>
            <p>
              We reserve the right to modify subscription prices. Existing subscribers will 
              be notified at least 30 days before any price change takes effect.
            </p>
          </section>

          <section>
            <h2>6. Acceptable Use</h2>
            <p>You agree NOT to use the Service to:</p>
            <ul>
              <li>Create fraudulent, false, or misleading documents</li>
              <li>Document illegal transactions or activities</li>
              <li>Impersonate another person or entity</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated means to access the Service without permission</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section>
            <h2>7. Document Accuracy</h2>
            <p>
              You are solely responsible for the accuracy and completeness of information 
              entered into bills of sale and invoices. PrivateSold does not verify the 
              information you provide and is not responsible for errors, omissions, or 
              inaccuracies in your documents.
            </p>
          </section>

          <section>
            <h2>8. Electronic Signatures</h2>
            <p>
              Our e-signature feature allows parties to electronically sign documents. 
              By using this feature, you acknowledge that:
            </p>
            <ul>
              <li>Electronic signatures may have legal effect similar to handwritten signatures in many jurisdictions</li>
              <li>You are responsible for understanding the legal implications in your jurisdiction</li>
              <li>We provide the technical means for signing but do not guarantee legal enforceability</li>
              <li>Both parties should consent to the use of electronic signatures</li>
            </ul>
          </section>

          <section>
            <h2>9. Intellectual Property</h2>
            <h3>9.1 Our Content</h3>
            <p>
              The Service, including its design, features, and content, is owned by 
              PrivateSold and protected by copyright, trademark, and other intellectual 
              property laws.
            </p>
            
            <h3>9.2 Your Content</h3>
            <p>
              You retain ownership of the information you enter into documents. By using 
              our Service, you grant us a limited license to process and store this 
              information as necessary to provide the Service.
            </p>
          </section>

          <section>
            <h2>10. Disclaimers</h2>
            <p><strong>NO LEGAL ADVICE:</strong> PrivateSold is not a law firm and does not provide legal advice. The documents generated are templates for general use. For legal questions about your specific situation, consult a licensed attorney.</p>
            
            <p><strong>AS-IS SERVICE:</strong> The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>
            
            <p><strong>NO GUARANTEE:</strong> We do not guarantee that documents created will be accepted by any third party, government agency, or court. Requirements vary by jurisdiction.</p>
          </section>

          <section>
            <h2>11. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, PrivateSold and its officers, 
              directors, employees, and agents shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, including but 
              not limited to loss of profits, data, or other intangible losses, 
              resulting from:
            </p>
            <ul>
              <li>Your use or inability to use the Service</li>
              <li>Any errors or omissions in documents you create</li>
              <li>Unauthorized access to or alteration of your data</li>
              <li>Any third party conduct on the Service</li>
            </ul>
            <p>
              Our total liability shall not exceed the amount you paid us in the 12 months 
              preceding the claim.
            </p>
          </section>

          <section>
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless PrivateSold and its affiliates 
              from any claims, damages, losses, or expenses (including legal fees) 
              arising from your use of the Service, violation of these Terms, or 
              infringement of any third party rights.
            </p>
          </section>

          <section>
            <h2>13. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with 
              or without cause, with or without notice. Upon termination, your right to 
              use the Service ceases immediately.
            </p>
            <p>
              You may delete your account at any time through your account settings. 
              Upon deletion, your data will be removed in accordance with our Privacy Policy.
            </p>
          </section>

          <section>
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws 
              of the United States, without regard to conflict of law principles. Any 
              disputes shall be resolved in the courts of competent jurisdiction.
            </p>
          </section>

          <section>
            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide 
              notice of significant changes by posting on our website or emailing you. 
              Continued use of the Service after changes constitutes acceptance of the 
              modified Terms.
            </p>
          </section>

          <section>
            <h2>16. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining 
              provisions will continue in full force and effect.
            </p>
          </section>

          <section>
            <h2>17. Contact Us</h2>
            <p>If you have questions about these Terms of Service, please contact us at:</p>
            <ul className="contact-list">
              <li>Email: support@privatesold.com</li>
              <li>Website: privatesold.com</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
